import { CellRange, DataMap } from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { FlexGridFilter } from '@grapecity/wijmo.react.grid.filter';
import TextComponent from 'components/Input/TextComponent';
import { useAuth } from 'context/AuthContext';
import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import ScreenTransitionMenu from 'components/Navigation/ScreenTransitionMenu';
import SelectComponent from 'components/Input/SelectComponent';
import { FlexGridXlsxConverter } from '@grapecity/wijmo.grid.xlsx';
import ConstructionProgressEditModal from 'views/ConstructionRequestList/ConstructionProgressEditModal';

import { getAgeCategory } from 'utils/ageUtils';
import { getFirstDayOfLastMonth } from 'utils/dateUtils';

import {
  ClientType,
  ClientTypes,
  ProjectTypes,
  KoujiKbn1,
  KoujiKbn2,
  VisitReason,
  Areas,
  AgeCategoryWithClientTypes,
} from 'constants/constants';

// 見積依頼日(開始)の既定値：前月1日
const getDefaultUkeDateStart = () => {
  const d = getFirstDayOfLastMonth(new Date());
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// 検索条件の初期値
const getDefaultSearchCondition = () => ({
  UKE_DATE_START: getDefaultUkeDateStart(),
  UKE_DATE_END: '',
  KEYWORD: '',
});

// 見積依頼台帳
const QuoteRequestList = (props) => {
  const auth = useAuth();
  const restApi = useRestApi();
  const location = useLocation();
  // 履歴
  const navigate = useNavigate();
  // グリッド
  const flexGrid = useRef();

  // 見積依頼台帳一覧
  const [quoteRequests, setQuoteRequests] = useState([]);

  // 選択中の工事No
  const [targetKoujiNo, setTargetKoujiNo] = useState();

  // 一覧の件数
  const [count, setCount] = useState(0);

  // 一覧の全件数
  const [total, setTotal] = useState(0);

  // 顧客検索条件
  const [searchCondition, setSearchCondition] = useState(getDefaultSearchCondition());

  // 工事状況モーダルへのパラメタ
  const [constructionProgressEditModalParam, setConstructionProgressEditModalParam] = useState({});

  // マスタ情報
  const master = useMaster();

  // 年間管理で絞るかチェック
  const [isAnnualManagement, setIsAnnualManagement] = useState(false);
  // 顧客区分公共のみで絞るかチェック
  const [isClientTypePublicOnly, setIsClientTypePublicOnly] = useState(false);

  // 検索処理
  const SearchClients = (condition, targetKoujiNo = null) => {
    // 年間管理の条件を追加
    const searchCondition = {
      ...condition,
      IS_ANNUAL_MANAGEMENT: isAnnualManagement,
      IS_CLIENT_TYPE_PUBLIC_ONLY: isClientTypePublicOnly,
    };
    restApi.post('/api/quoteRequest/list', searchCondition, (data) => {
      const _quoteRequests = data.baseQuotes.map((quoteRequest) => {
        return {
          ...quoteRequest,
          // 年代を取得
          AGE_CATEGORY: getAgeCategory(null, quoteRequest.CLIENT_TYPE, quoteRequest.AGE_CATEGORY),
          ADDRESS_ALL: `${quoteRequest.PREF == '群馬県' ? '' : quoteRequest.PREF}${quoteRequest.CITY
            }${quoteRequest.TOWN}${quoteRequest.ADDRESS}`,
        };
      });
      setTotal(data.total);
      setQuoteRequests(_quoteRequests);
      setTargetKoujiNo(targetKoujiNo);
    });
  };

  // 初期表示処理
  useEffect(() => {
    // 別画面から戻ってきた場合は検索条件を復活
    let condition = location?.state?.searchCondition ?? getDefaultSearchCondition();
    setSearchCondition(condition);
    // 検索
    SearchClients(condition, location?.state?.targetClientCd);
  }, []);

  // 他の画面から戻ってきた場合に顧客を選択
  useEffect(() => {
    selectTarget(targetKoujiNo);
  }, [targetKoujiNo]);

  const selectTarget = (targetKoujiNo) => {
    if (targetKoujiNo) {
      const grid = flexGrid.current.control;

      // 対象のSEIKYU_NOを持つ行のインデックスを探す
      const index = grid.rows.findIndex((row) => row.dataItem?.SEIKYU_NO === targetKoujiNo);

      if (index >= 0) {
        setTimeout(() => {
          grid.select(new CellRange(index), true); // 対象行を選択状態にする
        }, 10);
      }
    }
  };

  // コード翻訳用のマッピング
  const planerWithEmpty = [...master.planer, { id: 0, name: '' }];
  const planerMap = new DataMap(planerWithEmpty, 'id', 'name');
  var projectTypesMap = new DataMap(ProjectTypes, 'key', 'value');
  var clientTypeMap = new DataMap(ClientTypes, 'key', 'value');
  var koujiKbn1Map = new DataMap(KoujiKbn1, 'key', 'value');
  var koujiKbn2Map = new DataMap(KoujiKbn2, 'key', 'value');
  var ukeTypeMap = new DataMap(master.ukeType, 'id', 'name');
  var visitReasonMap = new DataMap(VisitReason, 'key', 'value');
  var areaMap = new DataMap(Areas, 'key', 'value');
  var ageCategoryMap = new DataMap(AgeCategoryWithClientTypes, 'key', 'value');
  var constructionStatusMap = new DataMap(master.constructionStatus, 'id', 'name');

  // 顧客情報ボタン
  const handleOnClickClientInfo = useCallback(() => {
    navigate('/admin/ClientInfo', {
      state: {
        clientId: flexGrid.current.control.selectedItems[0]['CLIENT_CD'],
        seikyuNo: flexGrid.current.control.selectedItems[0]['SEIKYU_NO'],
      },
    });
  }, [navigate]);

  // 工事状況変更ボタン
  const handleOnClickStatusEdit = () => {
    const grid = flexGrid.current.control;
    const collectionView = grid.collectionView;

    // 工事状況変更モーダルの呼び出し処理
    setConstructionProgressEditModalParam({
      call_list: true,
      constructionData: grid.selectedItems[0], // 現在選択されている行のデータを渡す
      // 工事状況更新後のコールバック関数
      callback: (construction) => {
        // ステータスを数値型に変換
        const updatedQuoteRequest = {
          ...construction,
          CONSTRUCTION_STATUS: Number(construction.CONSTRUCTION_STATUS),
        };

        // SEIKYU_NOが一致する行を検索
        const target = collectionView.items.find(
          (item) => item.SEIKYU_NO === construction.SEIKYU_NO
        );

        if (target) {
          // 対象行のステータスを直接上書き
          Object.assign(target, updatedQuoteRequest);

          //　再描画して変更を反映
          collectionView.refresh();

          // 編集した行を再選択
          selectTarget(construction.SEIKYU_NO);
        }
      },
    });
  };

  // 検索ボタン
  const handleOnClickSearch = () => {
    SearchClients(searchCondition);
  };

  // 編集処理
  const handleChange = (prop) => (target) => {
    setSearchCondition({ ...searchCondition, [prop]: target?.value ?? target?.target.value });
  };

  // Excelダウンロード
  const handleExcelDownload = () => {
    const grid = flexGrid.current.control; // FlexGridコンポーネントへの参照を取得
    const fileName = '見積依頼台帳.xlsx'; // ダウンロードするExcelファイルの名前

    // Excelファイルを生成し、ダウンロードする
    FlexGridXlsxConverter.save(
      grid,
      {
        includeColumns: function (column) {
          return column.header; // ヘッダー名称なしは含めない（ボタンの項目）
        },
      },
      fileName
    );
  };

  // フィルタの変更処理
  const filterApplied = (s, e) => {
    let grid = s.grid;
    setCount(grid?.collectionView?.items.length);
  };

  // フォームのsubmit処理
  const handleOnSubmitSearch = (e) => {
    e.preventDefault();
    handleOnClickSearch();
  };

  return (
    <>
      <Card className="m-0 table-container">
        <Card.Body>
          <Row>
            <Col>
              <form onSubmit={handleOnSubmitSearch}>
                <Row>
                  <Col className="pl-3 pr-2" xs="auto" style={{ width: '300px' }}>
                    <Form.Group>
                      <label>絞り込み</label>
                      <TextComponent
                        placeholder="顧客名・住所・電話番号"
                        type="text"
                        value={searchCondition.KEYWORD}
                        onChange={handleChange('KEYWORD')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="2.5">
                    <Form.Group>
                      <label>見積依頼日</label>
                      <div className="d-flex" style={{ width: '340px' }}>
                        <TextComponent
                          type="date"
                          value={searchCondition.UKE_DATE_START}
                          onChange={handleChange('UKE_DATE_START')}
                        ></TextComponent>
                        <span className="mt-2 ml-2 mr-2">～</span>
                        <TextComponent
                          type="date"
                          value={searchCondition.UKE_DATE_END}
                          onChange={handleChange('UKE_DATE_END')}
                        ></TextComponent>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="1.5" style={{ width: '200px' }}>
                    <Form.Group>
                      <label>営業担当</label>
                      <SelectComponent
                        value={searchCondition.SALES_TAN_CD}
                        onChange={handleChange('SALES_TAN_CD')}
                      >
                        <option value={''}></option>
                        {master.salesStaff.map((x, i) => (
                          <option key={i} value={x['id']}>
                            {x['name']}
                          </option>
                        ))}
                      </SelectComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="1.5" style={{ width: '200px' }}>
                    <Form.Group>
                      <label>ステータス</label>
                      <SelectComponent
                        value={searchCondition.CONSTRUCTION_STATUS}
                        onChange={handleChange('CONSTRUCTION_STATUS')}
                      >
                        <option value={''}></option>
                        {master.constructionStatus.map((x, i) => (
                          <option key={i} value={x['id']}>
                            {x['name']}
                          </option>
                        ))}
                      </SelectComponent>
                    </Form.Group>
                  </Col>
                  <Col className="mt-2 pl-2 pr-2" md="1.5">
                    <Form.Group>
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isAnnualManagement}
                          onChange={() => setIsAnnualManagement(!isAnnualManagement)}
                        ></input>
                        年間管理のみ表示
                      </label>
                      <br />
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isClientTypePublicOnly}
                          onChange={() => setIsClientTypePublicOnly(!isClientTypePublicOnly)}
                        ></input>
                        公共で絞る
                      </label>
                    </Form.Group>
                  </Col>
                  <Col className="mt-1">
                    <Form.Group>
                      <br />
                      <Button
                        variant="success"
                        className="btn-fill"
                        size="sm"
                        type="submit"
                      >
                        検索
                      </Button>
                      {/* 入力中の検索条件を引き継いで各一覧へ遷移する */}
                      <ScreenTransitionMenu
                        current="quoteRequests"
                        searchCondition={searchCondition}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </form>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                variant="success"
                className="btn-fill m-2"
                size="sm"
                onClick={handleExcelDownload}
              >
                Excelダウンロード
              </Button>
              件数：{count}件 / 全{total}件
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <div className="table-scroll">
                <FlexGrid
                  ref={flexGrid}
                  isReadOnly={true}
                  itemsSource={quoteRequests}
                  selectionMode={'Row'}
                  headersVisibility={'Column'}
                  style={{ height: '100%' }}
                  frozenColumns={4}
                  loadedRows={(grid) => {
                    grid.rows.forEach((ele) => {
                      ele.height = 38;
                    });
                  }}
                >
                  <FlexGridFilter filterApplied={filterApplied} />
                  <FlexGridColumn header="" binding="" isReadOnly={true} width={84}>
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="success"
                            title="顧客情報"
                            onClick={handleOnClickClientInfo}
                          >
                            person
                          </Button>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="light"
                            title="工事状況編集"
                            onClick={handleOnClickStatusEdit}
                          >
                            more_horiz
                          </Button>
                        </span>
                      )}
                    />
                  </FlexGridColumn>
                  <FlexGridColumn
                    binding="CONSTRUCTION_STATUS"
                    header="ステータス"
                    width={82}
                    dataMap={constructionStatusMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="UKE_DATE"
                    header="見積依頼日"
                    width={82}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="CLIENT_NAME"
                    header="顧客名１"
                    width={140}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="REPRESENTATIVE"
                    header="担当"
                    width={80}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="KOUJI_MEI" header="工事名" width={180}></FlexGridColumn>
                  <FlexGridColumn binding="IRAI_MEI" header="依頼内容" width={180}></FlexGridColumn>
                  <FlexGridColumn
                    binding="CONTRACT_NUMBER"
                    header="契約番号"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="ZEINUKI_KIN"
                    header="契約金額(税抜)"
                    allowResizing={false}
                    dataType="Number"
                    width={120}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="SEIKYU_KINGAKU"
                    header="金額変更(税抜)"
                    dataType="Number"
                    width={120}
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="FINAL_AMOUNT"
                    header="最終金額(税抜)"
                    dataType="Number"
                    width={120}
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="GENBA_TAN_CD"
                    header="現場担当"
                    allowResizing={false}
                    dataMap={planerMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KOUKI_ST_ACT"
                    header="着工日"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="HIKI_DATE"
                    header="引渡し日"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="POST_CODE" header="〒" width={74}></FlexGridColumn>
                  <FlexGridColumn binding="ADDRESS_ALL" header="住所" width={200}></FlexGridColumn>
                  <FlexGridColumn binding="TEL1" header="TEL"></FlexGridColumn>
                  <FlexGridColumn
                    binding="PROJECT_TYPE"
                    header="区分①"
                    width={60}
                    allowResizing={false}
                    dataMap={projectTypesMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="CLIENT_TYPE"
                    header="区分②"
                    width={60}
                    allowResizing={false}
                    dataMap={clientTypeMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KOUJI_KBN1"
                    header="区分③"
                    width={60}
                    allowResizing={false}
                    dataMap={koujiKbn1Map}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KOUJI_KBN2"
                    header="区分④"
                    width={80}
                    allowResizing={false}
                    dataMap={koujiKbn2Map}
                  ></FlexGridColumn>

                  <FlexGridColumn
                    binding="UKE_TAN_CD"
                    header="受付"
                    width={80}
                    allowResizing={false}
                    dataMap={planerMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="SALES_TAN_CD"
                    header="営業"
                    width={80}
                    allowResizing={false}
                    dataMap={planerMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="UKE_TYPE"
                    header="受付方法"
                    width={130}
                    allowResizing={false}
                    dataMap={ukeTypeMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="VISIT_REASON"
                    header="来店きっかけ"
                    width={200}
                    allowResizing={false}
                    dataMap={visitReasonMap}
                  >
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => {
                        const reason = visitReasonMap.getDisplayValue(cell.item.VISIT_REASON) || '';
                        const biko = cell.item.VISIT_REASON_BIKO || '';
                        return <span>{biko ? `${reason}（${biko}）` : reason}</span>;
                      }}
                    />
                  </FlexGridColumn>
                  <FlexGridColumn binding="KEI_DATE" header="結果"></FlexGridColumn>
                  <FlexGridColumn
                    binding="AGE_CATEGORY"
                    dataMap={ageCategoryMap}
                    header="年代(登録時)"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="NAME_KANA_1" header="フリガナ"></FlexGridColumn>
                  <FlexGridColumn binding="AREA" header="地域" dataMap={areaMap}></FlexGridColumn>
                  <FlexGridColumn binding="CLIENT_BIKO" header="備考" width={200}></FlexGridColumn>
                  {/* <FlexGridColumn header="" binding="" isReadOnly={true} width={80}>
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="primary"
                            title="編集"
                            onClick={handleOnClickEditClient}
                          >
                            edit
                          </Button>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="danger"
                            title="削除"
                            onClick={handleOnClickDeleteClient}
                          >
                            delete
                          </Button>
                        </span>
                      )}
                    />
                  </FlexGridColumn>
                  */}
                </FlexGrid>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {/** 工事状況モーダル */}
      <ConstructionProgressEditModal params={constructionProgressEditModalParam} />
    </>
  );
};

export default QuoteRequestList;
